import { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArrowLeft } from 'lucide-react-native';
import { ApiError } from '../api/client';
import {
  applyPhotoSearchStreamEvent,
  appendPhotoSearchFollowUpTurn,
  consumePhotoSearchStream,
  createDemoPhotoSearchFinalEvent,
  createInitialPhotoSearchConversation,
  createPhotoSearchSession,
  matchPhotoSearchProblems,
  replyToPhotoSearchSession,
  solvePhotoSearchProblem,
  uploadPhotoSearchImage,
} from '../api/photoSearch';
import { saveWrongQuestionFromPhotoSearch } from '../api/wrongQuestions';
import { CameraPreviewScreen } from '../components/business/CameraPreviewScreen';
import { OCRResultConfirmScreen } from '../components/business/OCRResultConfirmScreen';
import { PhotoSearchConversation } from '../components/business/PhotoSearchConversation';
import { PhotoSearchProcessingState } from '../components/business/PhotoSearchProcessingState';
import { AppButton } from '../components/ui/AppButton';
import { showAppToast } from '../components/ui/AppToast';
import { AppText } from '../components/ui/AppText';
import { ErrorView } from '../components/ui/ErrorView';
import { ScreenBackground } from '../components/ui/ScreenBackground';
import { DIAGRAM_API_MODE } from '../config/diagramApiConfig';
import { tokens } from '../theme/tokens';
import type {
  PhotoSearchConversationViewModel,
  PhotoSearchImageAssetViewModel,
  PhotoSearchImageSource,
  PhotoSearchMatchViewModel,
  PhotoSearchProcessingStep,
  PhotoSearchStreamEventDto,
} from '../types/photoSearch';
import {
  createConfirmedOcrMatch,
  getMatchReviewStep,
} from '../utils/photoSearchFlow';
import {
  clearPhotoSearchResumeState,
  loadPhotoSearchResumeState,
  savePhotoSearchResumeState,
} from '../utils/photoSearchResume';
import { resolvePhotoSearchDiagramContext } from '../utils/photoSearchDiagramContext';
import { recordPilotFlowFinished, recordPilotFlowStarted } from '../utils/pilotTelemetry';

type PhotoSearchMode = 'capture' | 'matches' | 'conversation';
type PhotoSearchStatus = 'loading' | 'error' | 'empty' | 'success';

type PhotoSearchPageState = {
  status: PhotoSearchStatus;
  mode: PhotoSearchMode;
  selectedImage: PhotoSearchImageAssetViewModel | null;
  imageRef: string | null;
  matches: PhotoSearchMatchViewModel[];
  conversation: PhotoSearchConversationViewModel | null;
  message: string;
};

type WebImagePickerAsset = ImagePicker.ImagePickerAsset & {
  file?: Blob | null;
  mimeType?: string | null;
};

const initialState: PhotoSearchPageState = {
  status: 'empty',
  mode: 'capture',
  selectedImage: null,
  imageRef: null,
  matches: [],
  conversation: null,
  message: '',
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ApiError) {
    if (error.code === 'unauthorized') {
      return '登录状态已失效，请重新登录';
    }
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const isTimeoutLikeError = (error: unknown): boolean => {
  if (error instanceof ApiError) {
    if (
      error.code === 'agent_timeout' ||
      error.code === 'agent_unavailable' ||
      error.code === 'agent_upstream_error'
    ) {
      return true;
    }
    return /timeout|timed out|超时/i.test(error.message);
  }
  if (error instanceof Error) {
    return /timeout|timed out|超时/i.test(error.message);
  }
  return false;
};

const getPhotoSearchFailureStep = (error: unknown): PhotoSearchProcessingStep =>
  isTimeoutLikeError(error) ? 'agent_timeout' : 'failed';

const getPilotErrorCategory = (error: unknown) => {
  if (isTimeoutLikeError(error)) return 'timeout' as const;
  if (error instanceof ApiError && error.code === 'network') return 'network' as const;
  if (error instanceof ApiError && error.code === 'server') return 'upstream' as const;
  if (error instanceof ApiError && error.code === 'parse') return 'validation' as const;
  return 'unknown_redacted' as const;
};

const RECOVERABLE_ERROR_STEPS: PhotoSearchProcessingStep[] = [
  'permission_denied',
  'failed',
  'agent_timeout',
];

const mapPickerAsset = (
  asset: ImagePicker.ImagePickerAsset
): PhotoSearchImageAssetViewModel => {
  const webAsset = asset as WebImagePickerAsset;
  return {
    uri: asset.uri,
    fileName: asset.fileName ?? null,
    mimeType: webAsset.mimeType ?? null,
    file: webAsset.file ?? null,
    width: typeof asset.width === 'number' ? asset.width : null,
    height: typeof asset.height === 'number' ? asset.height : null,
  };
};

export default function PhotoSearchPage() {
  const router = useRouter();
  const nativeStreamAbortRef = useRef<AbortController | null>(null);
  const connectedRequestRef = useRef<string | null>(null);
  const demoTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const searchFlowStartedAtRef = useRef<number | null>(null);
  const qaFlowStartedAtRef = useRef<number | null>(null);
  const completedPilotRequestRef = useRef<string | null>(null);
  const [state, setState] = useState<PhotoSearchPageState>(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [followUpText, setFollowUpText] = useState('');
  const [processingStep, setProcessingStep] = useState<PhotoSearchProcessingStep>('idle');
  const [selectedCaptureSource, setSelectedCaptureSource] =
    useState<PhotoSearchImageSource>('camera');
  const [savedToWrongBook, setSavedToWrongBook] = useState(false);
  const [savingToWrongBook, setSavingToWrongBook] = useState(false);
  const [resumeLoaded, setResumeLoaded] = useState(false);

  const clearDemoTimers = useCallback(() => {
    demoTimersRef.current.forEach((timer) => clearTimeout(timer));
    demoTimersRef.current = [];
  }, []);

  const closeStream = useCallback((resetConnected = false) => {
    nativeStreamAbortRef.current?.abort();
    nativeStreamAbortRef.current = null;
    if (resetConnected) {
      connectedRequestRef.current = null;
    }
    clearDemoTimers();
  }, [clearDemoTimers]);

  const updateConversation = useCallback(
    (
      updater: (
        conversation: PhotoSearchConversationViewModel
      ) => PhotoSearchConversationViewModel
    ) => {
      setState((current) => {
        if (!current.conversation) {
          return current;
        }
        return {
          ...current,
          status: 'success',
          mode: 'conversation',
          conversation: updater(current.conversation),
        };
      });
    },
    []
  );

  const applyStreamEvent = useCallback(
    (event: PhotoSearchStreamEventDto | string) => {
      updateConversation((conversation) => applyPhotoSearchStreamEvent(conversation, event));
    },
    [updateConversation]
  );

  const scheduleDemoStream = useCallback(() => {
    const schedule = (delay: number, event: PhotoSearchStreamEventDto | string) => {
      const timer = setTimeout(() => applyStreamEvent(event), delay);
      demoTimersRef.current.push(timer);
    };

    schedule(120, { type: 'stage', data: { stage: 'solver', message: '正在生成解法' } });
    schedule(260, { type: 'delta', data: { delta: '已进入演示讲解模式。' } });
    schedule(520, createDemoPhotoSearchFinalEvent());
    schedule(620, { type: 'done', data: {} });
  }, [applyStreamEvent]);

  const connectAcceptedStream = useCallback(
    (requestId: string, streamUrl: string | null) => {
      closeStream();

      if (DIAGRAM_API_MODE === 'fixture') {
        scheduleDemoStream();
        return;
      }

      const controller = new AbortController();
      nativeStreamAbortRef.current = controller;
      void consumePhotoSearchStream(streamUrl ?? requestId, applyStreamEvent, controller.signal)
        .catch((error: unknown) => {
          if (controller.signal.aborted) {
            return;
          }
          const flowName = qaFlowStartedAtRef.current ? 'qa' : 'search';
          const startedAt = qaFlowStartedAtRef.current ?? searchFlowStartedAtRef.current;
          void recordPilotFlowFinished(flowName, 'failed', {
            errorCategory: getPilotErrorCategory(error),
            durationMs: startedAt ? Date.now() - startedAt : undefined,
          });
          qaFlowStartedAtRef.current = null;
          searchFlowStartedAtRef.current = null;
          setProcessingStep('agent_timeout');
          applyStreamEvent({
            type: 'error',
            data: { detail: getErrorMessage(error, 'AI 讲解流连接中断，请稍后重试。') },
          });
        })
        .finally(() => {
          if (nativeStreamAbortRef.current === controller) {
            nativeStreamAbortRef.current = null;
          }
        });
    },
    [applyStreamEvent, closeStream, scheduleDemoStream]
  );

  useEffect(() => {
    let active = true;
    void loadPhotoSearchResumeState<PhotoSearchPageState>()
      .then((restored) => {
        if (!active || !restored) return;
        setState(restored);
        if (restored.mode === 'matches') {
          setProcessingStep(getMatchReviewStep(restored.matches));
        } else if (restored.conversation?.loading) {
          setProcessingStep('solving');
        } else if (restored.status === 'error') {
          setProcessingStep('failed');
        } else if (restored.conversation) {
          setProcessingStep('success_handoff');
        }
      })
      .finally(() => {
        if (active) setResumeLoaded(true);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!resumeLoaded) return;
    if (state.imageRef || state.matches.length > 0 || state.conversation) {
      void savePhotoSearchResumeState(state);
    }
  }, [resumeLoaded, state]);

  useEffect(() => {
    const conversation = state.conversation;
    if (!conversation?.requestId || !conversation.loading) {
      return;
    }
    if (connectedRequestRef.current === conversation.requestId) {
      return;
    }

    connectedRequestRef.current = conversation.requestId;
    connectAcceptedStream(conversation.requestId, conversation.streamUrl);
  }, [
    connectAcceptedStream,
    state.conversation?.loading,
    state.conversation?.requestId,
    state.conversation?.streamUrl,
  ]);

  useEffect(() => {
    const conversation = state.conversation;
    if (!conversation?.requestId || conversation.loading) return;
    if (completedPilotRequestRef.current === conversation.requestId) return;
    if (!conversation.messages.some((message) => message.kind === 'assistant_final')) return;

    completedPilotRequestRef.current = conversation.requestId;
    if (qaFlowStartedAtRef.current) {
      void recordPilotFlowFinished('qa', 'passed', {
        durationMs: Date.now() - qaFlowStartedAtRef.current,
      });
      qaFlowStartedAtRef.current = null;
      return;
    }
    if (searchFlowStartedAtRef.current) {
      void recordPilotFlowFinished('search', 'passed', {
        durationMs: Date.now() - searchFlowStartedAtRef.current,
      });
      searchFlowStartedAtRef.current = null;
    }
  }, [state.conversation]);

  useEffect(() => () => closeStream(true), [closeStream]);

  const pickImage = useCallback(
    async (source: PhotoSearchImageSource) => {
      if (source !== 'album') {
        setSelectedCaptureSource(source);
      }
      closeStream(true);
      setIsSubmitting(false);
      setFollowUpText('');
      setProcessingStep('idle');
      setSavedToWrongBook(false);
      setSavingToWrongBook(false);

      const permission =
        source === 'camera'
          ? await ImagePicker.requestCameraPermissionsAsync()
          : await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (!permission.granted) {
        setProcessingStep('permission_denied');
        setState({
          ...initialState,
          status: 'error',
          message: '需要授权后才能选择题目图片。',
        });
        return;
      }

      const picker =
        source === 'camera'
          ? ImagePicker.launchCameraAsync
          : ImagePicker.launchImageLibraryAsync;

      const response = await picker({
        allowsEditing: false,
        quality: 0.9,
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
      });

      if (!response.canceled && response.assets[0]) {
        setProcessingStep('idle');
        setState({
          status: 'success',
          mode: 'capture',
          selectedImage: mapPickerAsset(response.assets[0]),
          imageRef: null,
          matches: [],
          conversation: null,
          message: '',
        });
      }
    },
    [closeStream]
  );

  const handleToggleFlash = useCallback(() => {
    showAppToast('闪光灯会在接入真实相机预览后生效', 'info');
  }, []);

  const startSolve = useCallback(
    async (
      selectedImage: PhotoSearchImageAssetViewModel,
      imageRef: string,
      selectedMatch: PhotoSearchMatchViewModel | null,
      initialStep: PhotoSearchProcessingStep = 'solving',
      loadingMessage = '正在创建 AI 讲解会话'
    ) => {
      closeStream(true);
      setIsSubmitting(true);
      setProcessingStep(initialStep);
      setState((current) => ({
        ...current,
        status: 'loading',
        mode: 'conversation',
        selectedImage,
        imageRef,
        message: loadingMessage,
      }));

      try {
        const sessionId = await createPhotoSearchSession();
        const accepted = await solvePhotoSearchProblem(sessionId, imageRef, selectedMatch);
        setFollowUpText('');
        setProcessingStep('success_handoff');
        setState((current) => ({
          status: 'success',
          mode: 'conversation',
          selectedImage,
          imageRef,
          matches: current.matches,
          conversation: createInitialPhotoSearchConversation(
            selectedImage,
            imageRef,
            sessionId,
            accepted,
            selectedMatch
          ),
          message: '',
        }));
      } catch (error: unknown) {
        if (searchFlowStartedAtRef.current) {
          void recordPilotFlowFinished('search', 'failed', {
            errorCategory: getPilotErrorCategory(error),
            durationMs: Date.now() - searchFlowStartedAtRef.current,
          });
          searchFlowStartedAtRef.current = null;
        }
        const failureStep = getPhotoSearchFailureStep(error);
        if (failureStep === 'agent_timeout') {
          setProcessingStep('agent_timeout');
        } else {
          setProcessingStep('failed');
        }
        setState((current) => ({
          ...current,
          status: 'error',
          selectedImage,
          imageRef,
          message: getErrorMessage(error, '搜题请求失败，请稍后重试。'),
        }));
      } finally {
        setIsSubmitting(false);
      }
    },
    [closeStream]
  );

  const submitImage = useCallback(async () => {
      if (!state.selectedImage) {
      setProcessingStep('failed');
      setState({
        ...initialState,
        status: 'error',
        message: '请先拍照或从相册选择一道题。',
      });
      return;
    }

    const selectedImage = state.selectedImage;
    searchFlowStartedAtRef.current = Date.now();
    completedPilotRequestRef.current = null;
    void recordPilotFlowStarted('search');
    closeStream(true);
    setIsSubmitting(true);
    setProcessingStep('uploading');
    setSavedToWrongBook(false);
    setSavingToWrongBook(false);
    setState((current) => ({
      ...current,
      status: 'loading',
      mode: 'capture',
      selectedImage,
      message: '正在上传题图',
    }));

    try {
      const upload = await uploadPhotoSearchImage(selectedImage);
      setProcessingStep('recognizing');
      setState((current) => ({
        ...current,
        status: 'loading',
        mode: 'capture',
        selectedImage,
        imageRef: upload.imageRef,
        message: '正在识别题目',
      }));

      let matches: PhotoSearchMatchViewModel[] = [];
      try {
        setProcessingStep('matching');
        setState((current) => ({
          ...current,
          status: 'loading',
          mode: 'capture',
          selectedImage,
          imageRef: upload.imageRef,
          message: '正在匹配题库',
        }));
        matches = await matchPhotoSearchProblems(upload.imageRef);
      } catch (error: unknown) {
        if (DIAGRAM_API_MODE === 'fixture') {
          matches = [];
        } else {
          throw error;
        }
      }

      if (matches.length > 0) {
        const reviewStep = getMatchReviewStep(matches);
        if (reviewStep === 'low_confidence') {
          setProcessingStep('low_confidence');
        } else {
          setProcessingStep('idle');
        }
        setState({
          status: 'success',
          mode: 'matches',
          selectedImage,
          imageRef: upload.imageRef,
          matches,
          conversation: null,
          message: '',
        });
        return;
      }

      setProcessingStep('no_match');
      await startSolve(
        selectedImage,
        upload.imageRef,
        null,
        'no_match',
        '未匹配到题库，正在转入 AI 讲解'
      );
    } catch (error: unknown) {
      if (searchFlowStartedAtRef.current) {
        void recordPilotFlowFinished('search', 'failed', {
          errorCategory: getPilotErrorCategory(error),
          durationMs: Date.now() - searchFlowStartedAtRef.current,
        });
        searchFlowStartedAtRef.current = null;
      }
      const failureStep = getPhotoSearchFailureStep(error);
      if (failureStep === 'agent_timeout') {
        setProcessingStep('agent_timeout');
      } else {
        setProcessingStep('failed');
      }
      setState((current) => ({
        ...current,
        status: 'error',
        mode: 'capture',
        selectedImage,
        message: getErrorMessage(error, '搜题请求失败，请稍后重试。'),
      }));
    } finally {
      setIsSubmitting(false);
    }
  }, [closeStream, startSolve, state.selectedImage]);

  const skipMatch = useCallback(() => {
    if (!state.selectedImage || !state.imageRef) {
      return;
    }
    void startSolve(state.selectedImage, state.imageRef, null);
  }, [startSolve, state.imageRef, state.selectedImage]);

  const confirmOcrText = useCallback(
    (text: string, baseMatch: PhotoSearchMatchViewModel | null) => {
      if (!state.selectedImage || !state.imageRef) {
        return;
      }
      const confirmedMatch = createConfirmedOcrMatch(text, baseMatch);
      void startSolve(state.selectedImage, state.imageRef, confirmedMatch);
    },
    [startSolve, state.imageRef, state.selectedImage]
  );

  const sendFollowUp = useCallback(
    async (text: string) => {
      const messageText = text.trim();
      const conversation = state.conversation;
      if (!conversation || !messageText || conversation.loading) {
        return;
      }

      setFollowUpText('');
      setIsSubmitting(true);
      setProcessingStep('solving');
      qaFlowStartedAtRef.current = Date.now();
      completedPilotRequestRef.current = null;
      void recordPilotFlowStarted('qa');

      try {
        const accepted = await replyToPhotoSearchSession(conversation.sessionId, messageText);
        setProcessingStep('success_handoff');
        updateConversation((current) =>
          appendPhotoSearchFollowUpTurn(current, messageText, accepted)
        );
      } catch (error: unknown) {
        if (qaFlowStartedAtRef.current) {
          void recordPilotFlowFinished('qa', 'failed', {
            errorCategory: getPilotErrorCategory(error),
            durationMs: Date.now() - qaFlowStartedAtRef.current,
          });
          qaFlowStartedAtRef.current = null;
        }
        const failureStep = getPhotoSearchFailureStep(error);
        if (failureStep === 'agent_timeout') {
          setProcessingStep('agent_timeout');
        } else {
          setProcessingStep('failed');
        }
        updateConversation((current) =>
          applyPhotoSearchStreamEvent(current, {
            type: 'error',
            data: { detail: getErrorMessage(error, '追问提交失败，请稍后重试。') },
          })
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [state.conversation, updateConversation]
  );

  const handleReportAnswerIssue = useCallback(() => {
    updateConversation((current) =>
      applyPhotoSearchStreamEvent(current, {
        type: 'error',
        data: { detail: '已记录你的反馈，后续会用于优化这类讲解。' },
      })
    );
  }, [updateConversation]);

  const handleMarkHelpful = useCallback(() => {
    showAppToast('已记录满意反馈', 'success');
  }, []);

  const handleAddToWrongBook = useCallback(async () => {
    const conversation = state.conversation;
    if (!conversation || savingToWrongBook || savedToWrongBook) {
      return;
    }

    const selectedMatch = conversation.selectedMatch;
    const firstImpact = conversation.knowledgeImpacts[0] ?? null;
    const finalContents = conversation.messages
      .filter((message) => message.role === 'assistant' && message.kind === 'assistant_final')
      .map((message) => message.content)
      .filter(Boolean);

    setSavingToWrongBook(true);
    try {
      await saveWrongQuestionFromPhotoSearch({
        question:
          selectedMatch?.normalizedProblem ||
          selectedMatch?.ocrText ||
          selectedMatch?.title ||
          '拍照搜题题目',
        answer: finalContents[finalContents.length - 1] ?? null,
        steps: [],
        subject: firstImpact?.subject ?? selectedMatch?.subject ?? '综合',
        grade: selectedMatch?.grade ?? null,
        knowledgePoint:
          firstImpact?.knowledgePoint ?? selectedMatch?.knowledgePoints[0] ?? '未标注知识点',
        errorTag: 'AI 讲解后需复盘',
        customTags: ['拍照搜题'],
        imageRef: conversation.imageRef,
        problemId: conversation.problemId ?? selectedMatch?.id ?? null,
        sourceId: conversation.problemId ?? selectedMatch?.id ?? conversation.imageRef,
        sourceQuestionNo: selectedMatch?.questionNumber ?? null,
      });
      setSavedToWrongBook(true);
    } catch (error: unknown) {
      updateConversation((current) =>
        applyPhotoSearchStreamEvent(current, {
          type: 'error',
          data: { detail: getErrorMessage(error, '加入错题本失败，请稍后重试。') },
        })
      );
    } finally {
      setSavingToWrongBook(false);
    }
  }, [savedToWrongBook, savingToWrongBook, state.conversation, updateConversation]);

  const retrySolve = useCallback(async () => {
    const conversation = state.conversation;
    if (!conversation || conversation.loading) {
      return;
    }

    setIsSubmitting(true);
    setProcessingStep('retrying');
    try {
      const accepted = await solvePhotoSearchProblem(
        conversation.sessionId,
        conversation.imageRef,
        conversation.selectedMatch
      );
      setProcessingStep('success_handoff');
      updateConversation((current) =>
        appendPhotoSearchFollowUpTurn(current, '请重新生成这道题讲解', accepted)
      );
    } catch (error: unknown) {
      const failureStep = getPhotoSearchFailureStep(error);
      if (failureStep === 'agent_timeout') {
        setProcessingStep('agent_timeout');
      } else {
        setProcessingStep('failed');
      }
      updateConversation((current) =>
        applyPhotoSearchStreamEvent(current, {
          type: 'error',
          data: { detail: getErrorMessage(error, '重新生成失败，请稍后重试。') },
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [state.conversation, updateConversation]);

  const reset = useCallback(() => {
    closeStream(true);
    setFollowUpText('');
    setIsSubmitting(false);
    setProcessingStep('idle');
    setSavedToWrongBook(false);
    setSavingToWrongBook(false);
    setState(initialState);
    void clearPhotoSearchResumeState();
  }, [closeStream]);

  const retryAfterError = useCallback(() => {
    if (state.selectedImage) {
      void submitImage();
      return;
    }
    reset();
  }, [reset, state.selectedImage, submitImage]);

  const openDiagramQuestion = useCallback(() => {
    const conversation = state.conversation;
    if (!conversation) {
      return;
    }
    const context = resolvePhotoSearchDiagramContext(
      conversation.imageRef,
      conversation.selectedMatch,
      state.matches.length
    );
    if (!context) {
      showAppToast('当前题目缺少可确认的图形语义或选题裁剪，暂不生成图形', 'info');
      return;
    }
    router.push({
      pathname: '/diagram-question',
      params: {
        imageRef: context.imageRef,
        diagramType: context.diagramType,
        semanticProfile: context.semanticProfile,
        ...(context.selectedCropImageRef
          ? { selectedCropImageRef: context.selectedCropImageRef }
          : {}),
        selectedProblemId: context.selectedProblemId,
        ...(context.selectedSegmentId
          ? { selectedSegmentId: context.selectedSegmentId }
          : {}),
        ...(context.questionNumber ? { questionNumber: context.questionNumber } : {}),
      },
    });
  }, [router, state.conversation, state.matches.length]);

  const renderMainContent = () => {
    if (state.status === 'loading') {
      return (
        <PhotoSearchProcessingState
          step={processingStep}
          message={state.message || '正在处理题图'}
          onRetry={retryAfterError}
          onReset={reset}
        />
      );
    }

    if (
      state.status === 'error' &&
      RECOVERABLE_ERROR_STEPS.includes(processingStep)
    ) {
      return (
        <PhotoSearchProcessingState
          step={processingStep}
          message={state.message}
          onRetry={retryAfterError}
          onReset={reset}
        />
      );
    }

    if (state.status === 'error' && !state.selectedImage) {
      return <ErrorView message={state.message} onRetry={retryAfterError} />;
    }

    if (state.mode === 'conversation' && state.conversation) {
      return (
        <PhotoSearchConversation
          conversation={state.conversation}
          followUpText={followUpText}
          isSubmitting={isSubmitting}
          onFollowUpTextChange={setFollowUpText}
          onSendFollowUp={sendFollowUp}
          onRetry={retrySolve}
          onReset={reset}
          savedToWrongBook={savedToWrongBook}
          savingToWrongBook={savingToWrongBook}
          onMarkHelpful={handleMarkHelpful}
          onAddToWrongBook={handleAddToWrongBook}
          onReportAnswerIssue={handleReportAnswerIssue}
          onOpenDiagram={openDiagramQuestion}
        />
      );
    }

    if (state.mode === 'matches' && state.matches.length > 0) {
      return (
        <OCRResultConfirmScreen
          imageUri={state.selectedImage?.uri ?? null}
          matches={state.matches}
          processingStep={processingStep}
          isSubmitting={isSubmitting}
          onSkipMatch={skipMatch}
          onConfirmText={confirmOcrText}
          onRetake={reset}
        />
      );
    }

    return (
      <CameraPreviewScreen
        imageUri={state.selectedImage?.uri ?? null}
        activeSource={selectedCaptureSource}
        status={state.status}
        errorMessage={state.message}
        isSubmitting={isSubmitting}
        onChangeSource={setSelectedCaptureSource}
        onPickImage={pickImage}
        onSubmit={submitImage}
        onToggleFlash={handleToggleFlash}
        onRetry={retryAfterError}
      />
    );
  };

  return (
    <SafeAreaView style={styles.screen}>
      <ScreenBackground style={styles.background}>
        <View style={styles.header}>
          <AppButton
            label="首页"
            variant="ghost"
            onPress={() => router.push('/')}
            leftSlot={<ArrowLeft size={18} color={tokens.colors.primary} />}
            style={styles.backButton}
          />
          <View style={styles.headerTitle}>
            <AppText variant="title" weight="bold">
              拍照搜题
            </AppText>
            <AppText variant="caption" tone="muted">
              AI Agent 流式讲解
            </AppText>
          </View>
        </View>

        <ScrollView contentContainerStyle={styles.content}>{renderMainContent()}</ScrollView>
      </ScreenBackground>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  background: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.sm,
  },
  backButton: {
    minHeight: 40,
    paddingHorizontal: tokens.spacing.sm,
  },
  headerTitle: {
    flex: 1,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.xxl,
  },
});
